import type { Client } from 'src/users';
import { ClientRow } from 'src/UI/generate-flow/ClientRow';
import { SelectableList } from 'src/UI/generate-flow/SelectableList';
import { Family } from 'src/clients/hooks/useFamily';
import { Box, Flex, Paper, Radio, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

export type ClientListItem = {
  client: Client;
  lastReviewDateLabel?: string;
};

type ClientListProps = {
  items: ClientListItem[];
  selectedClientId?: string;
  onSelect: (client: Client) => void;
};

export function ClientList({ items, selectedClientId, onSelect }: ClientListProps) {
  return (
    <SelectableList
      items={items}
      getKey={i => i.client.id}
      renderRow={({ client, lastReviewDateLabel }) => (
        <ClientRow
          client={client}
          lastReviewDateLabel={lastReviewDateLabel}
          selected={client.id === selectedClientId}
          onSelect={onSelect}
        />
      )}
    />
  );
}

type FamilyListProps = {
  items: Family[];
  selectedFamilyId?: string;
  onSelect: (family: Family) => void;
};

export function FamilyList({ items, selectedFamilyId, onSelect }: FamilyListProps) {
  return (
    <SelectableList
      items={items}
      getKey={i => i.id}
      renderRow={family => (
        <FamilyRow family={family} selected={family.id === selectedFamilyId} onSelect={onSelect} />
      )}
    />
  );
}

type FamilyRowProps = {
  family: Family;
  lastReviewDateLabel?: string;
  selected: boolean;
  onSelect: (family: Family) => void;
};

function FamilyRow({ family, selected, onSelect }: FamilyRowProps) {
  return (
    <Paper
      radius="12"
      withBorder
      px="md"
      py="sm"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(family)}
      styles={theme => ({
        root: {
          cursor: 'pointer',
          borderColor: selected ? theme.colors.brand[6] : 'var(--default-border-color)',
          boxShadow: 'none',
          userSelect: 'none',
        },
      })}
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          columnGap: 'var(--mantine-spacing-xxs)',
        }}
      >
        <Flex align="center" gap="md" miw={0}>
          <Radio
            checked={selected}
            onChange={() => onSelect(family)}
            color="brand"
            size="md"
            iconColor="white"
          />

          <Text fw={FONT_WEIGHTS.semibold} size="sm" truncate c="gray.8">
            {family.name} Family
          </Text>
        </Flex>
      </Box>
    </Paper>
  );
}
